import { useEvents, useSessions } from "@/api/hooks";
import type {
  DeskRow as DeskRowType,
  EventEntry,
  SessionInfo,
} from "@/api/types";
import { deriveDeskState, deskAccentColor } from "@/api/types";
import { FileIcon, FolderIcon, UploadButton } from "./file-icon";
import {
  GhostSession,
  SessionPreview,
  SessionSkeleton,
} from "./session-preview";

function DeskLabel({
  text,
  expanded,
  loading,
}: {
  text: string;
  expanded: boolean;
  loading?: boolean;
}) {
  const color = expanded ? "var(--card-t3)" : "var(--t4)";
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--sp-1)",
        marginBottom: "2px",
      }}
    >
      <span
        style={{
          fontFamily: "var(--font-mono)",
          fontSize: "var(--text-2xs)",
          lineHeight: 1.5,
          letterSpacing: "0.5px",
          textTransform: "uppercase" as const,
          color,
          opacity: loading ? 0.5 : 1,
        }}
      >
        {text}
      </span>
      <svg
        width="8"
        height="8"
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth="2.5"
        style={{
          flexShrink: 0,
          transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
        }}
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </div>
  );
}

function DeskTitle({ name, expanded }: { name: string; expanded?: boolean }) {
  return (
    <h2
      style={{
        fontFamily: "var(--font-display)",
        fontSize: "var(--text-xl)",
        fontWeight: 600,
        lineHeight: "var(--lh-heading)",
        letterSpacing: "-0.5px",
        color: expanded ? "var(--card-t1)" : "var(--t1)",
      }}
      className={expanded ? "" : "group-hover:!text-[var(--accent)]"}
    >
      {name}
    </h2>
  );
}

interface DeskRowProps {
  desk: DeskRowType;
  index: number;
  expanded: boolean;
  onToggle: () => void;
  onOpen: (deskId: string, sessionId?: string) => void;
  /**
   * Called on hover/touchstart so the dashboard can warm the desk's queries
   * in the background, making the subsequent navigation feel instant.
   */
  onPrefetch?: (deskId: string) => void;
}

export function DeskRow({
  desk,
  index,
  expanded,
  onToggle,
  onOpen,
  onPrefetch,
}: DeskRowProps) {
  const accentColor = deskAccentColor(index);
  const state = deriveDeskState(desk);
  const { data: sessions, isLoading: sessionsLoading } = useSessions(desk.id);

  const hasSessions = sessions && sessions.length > 0;
  const hasRunningSessions = sessions?.some(
    (s) => s.agentStatus === "running" || s.agentStatus === "starting",
  );
  const sessionCount = sessions?.length ?? 0;

  const frogSrc =
    state === "active" && hasRunningSessions
      ? "/frogs/working.gif"
      : state === "active"
        ? "/frogs/sleeping.gif"
        : state === "waking" || state === "booting"
          ? "/frogs/waking.gif"
          : "/frogs/sleeping.gif";
  const labelText = sessionsLoading
    ? "···"
    : sessionCount > 0
      ? `${sessionCount} session${sessionCount !== 1 ? "s" : ""}`
      : "empty";

  const bg = expanded
    ? "var(--card-bg)"
    : `color-mix(in srgb, ${accentColor} 1.5%, var(--bg))`;
  const stripe = expanded
    ? "linear-gradient(180deg, var(--accent), var(--card-bg))"
    : `linear-gradient(180deg, ${accentColor}, color-mix(in srgb, ${accentColor} 15%, var(--bg)))`;

  return (
    <div
      className="desk-row-debug flex border-b border-[var(--desk-divider)] relative group"
      onMouseEnter={() => onPrefetch?.(desk.id)}
      onTouchStart={() => onPrefetch?.(desk.id)}
      style={{
        background: bg,
        height: expanded
          ? "auto"
          : hasSessions
            ? "var(--desk-session-default)"
            : "auto",
        overflow: "hidden",
      }}
    >
      <div className="w-[3px] flex-shrink-0" style={{ background: stripe }} />

      <div
        className="desk-files-debug"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--sp-1)",
          flexShrink: 0,
          padding: "var(--sp-5) var(--sp-2)",
          alignItems: "center",
          width: "48px",
        }}
      >
        <UploadButton />
      </div>

      <div
        style={{
          flex: 1,
          minWidth: 0,
          paddingTop: "var(--sp-4)",
          paddingBottom: "var(--sp-3)",
          paddingRight: "var(--sp-2)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="desk-title-debug"
          onClick={onToggle}
          style={{
            paddingLeft: "var(--sp-3)",
            marginBottom: "var(--sp-2)",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <DeskLabel
            text={labelText}
            expanded={expanded}
            loading={sessionsLoading}
          />
          <DeskTitle name={desk.name} expanded={expanded} />
        </div>

        <div
          className="hide-scrollbar session-strip-debug"
          style={{
            flex: 1,
            display: "flex",
            alignItems: "stretch",
            overflowX: "auto",
            scrollSnapType: "x proximity",
          }}
        >
          {sessionsLoading ? (
            <SessionSkeleton />
          ) : hasSessions ? (
            <>
              {sessions.map((session, si) => (
                <SessionWithEvents
                  key={session.sessionId}
                  deskId={desk.id}
                  session={session}
                  showBorder={si > 0}
                  expanded={expanded}
                  onClick={() => onOpen(desk.id, session.sessionId)}
                />
              ))}
              <GhostSession
                onClick={() => onOpen(desk.id)}
                inverted={expanded}
              />
            </>
          ) : (
            <EmptyDeskCTA onClick={() => onOpen(desk.id)} inverted={expanded} />
          )}
        </div>
      </div>

      <img
        src={frogSrc}
        alt=""
        className="frog-debug"
        style={{
          position: "absolute",
          top: "var(--sp-2)",
          right: "var(--sp-3)",
          width: "24px",
          height: "24px",
          opacity: 0.65,
        }}
      />
    </div>
  );
}

function EmptyDeskCTA({
  onClick,
  inverted,
}: {
  onClick: () => void;
  inverted?: boolean;
}) {
  return (
    <div
      onClick={onClick}
      style={{
        alignSelf: "flex-start",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "var(--sp-2)",
        padding: "var(--sp-2) var(--sp-4)",
        border: `1px dashed ${inverted ? "var(--card-border)" : "var(--border)"}`,
        borderRadius: "4px",
        margin: "var(--sp-1) var(--sp-3) 0",
        cursor: "pointer",
        color: inverted ? "var(--card-t3)" : "var(--t4)",
        fontFamily: "var(--font-ui)",
        fontSize: "var(--text-sm)",
      }}
    >
      <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="8" x2="12" y2="16" />
        <line x1="8" y1="12" x2="16" y2="12" />
      </svg>
      <span>Open desk and start working</span>
    </div>
  );
}

function SessionWithEvents({
  deskId,
  session,
  showBorder,
  expanded,
  onClick,
}: {
  deskId: string;
  session: SessionInfo;
  showBorder?: boolean;
  expanded?: boolean;
  onClick?: () => void;
}) {
  const isActive =
    session.agentStatus === "running" || session.agentStatus === "starting";
  const { data: events } = useEvents(deskId, session.sessionId, isActive);
  return (
    <div
      className="session-debug"
      style={{
        width: "min(26em, 74vw)",
        flexShrink: 0,
        flexGrow: 0,
        scrollSnapAlign: "start",
        display: "flex",
        overflow: "hidden",
      }}
    >
      {events ? (
        <SessionPreview
          events={events}
          agentStatus={session.agentStatus}
          lastActivityAt={session.lastActivityAt}
          showBorder={showBorder}
          expanded={expanded}
          inverted={expanded}
          onClick={onClick}
        />
      ) : (
        <SessionSkeleton />
      )}
    </div>
  );
}

export function DeskRowSkeleton() {
  return (
    <div
      className="flex border-b border-[var(--blt)]"
      style={{ height: "130px" }}
    >
      <div
        className="w-[3px] animate-pulse"
        style={{ background: "var(--border)" }}
      />
      <div
        style={{
          width: "48px",
          flexShrink: 0,
          padding: "var(--sp-5) var(--sp-2)",
          display: "flex",
          justifyContent: "center",
        }}
      >
        <div
          className="animate-pulse"
          style={{
            width: "26px",
            height: "26px",
            borderRadius: "3px",
            background: "var(--border)",
          }}
        />
      </div>
      <div
        style={{
          flex: 1,
          paddingTop: "var(--sp-4)",
          paddingBottom: "var(--sp-3)",
          paddingLeft: "var(--sp-1)",
        }}
      >
        <div
          className="animate-pulse"
          style={{
            height: "10px",
            width: "80px",
            background: "var(--border)",
            borderRadius: "2px",
            marginBottom: "var(--sp-1)",
            opacity: 0.4,
          }}
        />
        <div
          className="animate-pulse"
          style={{
            height: "22px",
            width: "220px",
            background: "var(--border)",
            borderRadius: "3px",
            marginBottom: "var(--sp-3)",
          }}
        />
        <div style={{ display: "flex", gap: "var(--sp-2)" }}>
          <div
            className="animate-pulse"
            style={{
              height: "80px",
              flex: 1,
              background: "var(--border)",
              borderRadius: "3px",
              opacity: 0.3,
            }}
          />
          <div
            className="animate-pulse"
            style={{
              height: "80px",
              flex: 1,
              background: "var(--border)",
              borderRadius: "3px",
              opacity: 0.2,
            }}
          />
        </div>
      </div>
    </div>
  );
}

/* ============================
   Test desk for ?debug mode
   ============================ */
export function TestDeskRow({
  expanded,
  onToggle,
  onOpen,
}: {
  expanded: boolean;
  onToggle: () => void;
  onOpen: (id: string, sid?: string) => void;
}) {
  const accentColor = "var(--da-1)";
  const msg = (user: string, agent: string): EventEntry[] => [
    { type: "user_prompt", prompt: user, timestamp: "2026-03-25T01:00:00Z" },
    {
      type: "assistant",
      message: { content: [{ type: "text", text: agent }] },
      timestamp: "2026-03-25T01:05:00Z",
    },
  ];
  const file = (f: string): EventEntry => ({
    type: "assistant",
    message: {
      content: [{ type: "tool_use", name: "Write", input: { file_path: f } }],
    },
    timestamp: "2026-03-25T01:04:00Z",
  });

  const sessions: {
    events: EventEntry[];
    status: "completed" | "failed" | "running";
  }[] = [
    {
      events: [
        ...msg(
          "Parse all the uploaded PDFs and extract the key financial metrics into a structured database.",
          "I've parsed all 14 PDF reports and created a SQLite database with 598 records. Each record contains company name, fiscal year, revenue, EBITDA, net income, and audit opinion.",
        ),
        file("/data/financials.db"),
        file("/data/summary.csv"),
        ...msg(
          "Great — now add a computed column for EBITDA margin percentage",
          "Done. Added `ebitda_margin_pct` as (EBITDA / Revenue * 100) for all 598 records. Ranges from -12% (CloudBase FY2020) to 41% (TechCorp FY2022).",
        ),
        ...msg(
          "Which companies had negative margins?",
          "Three companies had negative EBITDA margins: CloudBase in FY2020 (-12%, COVID writedowns), AIFirst in FY2019 (-3%, pre-revenue), and DataFlow in FY2020 (-1%, restructuring costs).",
        ),
      ],
      status: "completed",
    },
    {
      events: [
        ...msg(
          "Make charts comparing the top 5 companies by revenue growth. Use chart.js and save as PNGs.",
          "Created 3 comparison charts. Revenue growth leaders: TechCorp (+34%), DataFlow (+28%), CloudBase (+22%), SecureNet (+19%), and AIFirst (+17%). Charts saved to /data/charts/.",
        ),
        file("/data/charts/revenue-growth.png"),
        file("/data/charts/margins.png"),
      ],
      status: "completed",
    },
    {
      events: [
        ...msg(
          "Why is the EBITDA margin declining for SecureNet?",
          "SecureNet's EBITDA margin declined from 32% to 21% over 3 years, primarily due to aggressive hiring (+45% headcount) and infrastructure expansion into APAC markets.",
        ),
        ...msg(
          "Can you quantify the R&D impact specifically?",
          "R&D spend increased 2.3x while revenue only grew 1.4x. The hiring was front-loaded in FY2023 — they added 340 engineers in one quarter. APAC infrastructure costs added $12M/year in opex.",
        ),
        ...msg(
          "Is this sustainable? What does the trend suggest for FY2026?",
          "At current trajectory, margins would hit 15% by FY2026. However, if APAC revenue ramps as projected (they're guiding 40% YoY), margins should recover to 25% by FY2027. The key risk is whether APAC adoption materializes.",
        ),
      ],
      status: "completed",
    },
    {
      events: [
        ...msg(
          "Run the regression model on the full dataset",
          "Error: Missing dependency 'scipy'. The regression model requires scipy which isn't installed on this machine.",
        ),
      ],
      status: "failed",
    },
    {
      events: [
        ...msg(
          "Install scipy and retry the regression analysis",
          "Installed scipy via pip. Running the regression model now.",
        ),
        ...msg(
          "What does it show?",
          "Strong correlation (R²=0.87) between R&D investment ratio and revenue growth. Two outliers: CloudBase FY2020 (COVID) and TechCorp FY2024 (acquisition).",
        ),
        file("/data/regression-results.json"),
        file("/data/outlier-report.md"),
      ],
      status: "completed",
    },
    {
      events: [
        ...msg(
          "Now build a forecast model for the next 3 years using the regression results",
          "Building 3-year forecast using the validated regression model. Processing Monte Carlo simulations with 10,000 iterations for confidence intervals...",
        ),
      ],
      status: "running",
    },
  ];

  const bg = expanded
    ? "var(--card-bg)"
    : `color-mix(in srgb, ${accentColor} 1.5%, var(--bg))`;
  const stripe = expanded
    ? "linear-gradient(180deg, var(--accent), var(--card-bg))"
    : `linear-gradient(180deg, ${accentColor}, color-mix(in srgb, ${accentColor} 15%, var(--bg)))`;

  return (
    <div
      className="desk-row-debug flex border-b border-[var(--desk-divider)] relative group"
      style={{
        background: bg,
        height: expanded ? "auto" : "var(--desk-session-default)",
        overflow: "hidden",
      }}
    >
      <div className="w-[3px] flex-shrink-0" style={{ background: stripe }} />
      <div
        className="desk-files-debug"
        onClick={(e) => e.stopPropagation()}
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--sp-1)",
          flexShrink: 0,
          padding: "var(--sp-5) var(--sp-2)",
          alignItems: "center",
          width: "48px",
        }}
      >
        <FileIcon name="annual-2024.pdf" />
        <FileIcon name="quarterly.xlsx" />
        <FolderIcon name="reports" count={14} />
        <UploadButton />
      </div>
      <div
        style={{
          flex: 1,
          minWidth: 0,
          paddingTop: "var(--sp-4)",
          paddingBottom: "var(--sp-3)",
          paddingRight: "var(--sp-2)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          className="desk-title-debug"
          onClick={onToggle}
          style={{
            paddingLeft: "var(--sp-3)",
            marginBottom: "var(--sp-2)",
            cursor: "pointer",
            flexShrink: 0,
          }}
        >
          <DeskLabel text={`${sessions.length} sessions`} expanded={expanded} />
          <DeskTitle name="Financial Analysis (Test)" expanded={expanded} />
        </div>
        <div
          className="hide-scrollbar session-strip-debug"
          style={{
            flex: 1,
            display: "flex",
            alignItems: "stretch",
            overflowX: "auto",
            scrollSnapType: "x proximity",
          }}
        >
          {sessions.map((s, i) => (
            <div
              key={`test-ses-${i}`}
              className="session-debug"
              style={{
                width: "min(26em, 74vw)",
                flexShrink: 0,
                scrollSnapAlign: "start",
                display: "flex",
                overflow: "hidden",
              }}
            >
              <SessionPreview
                events={s.events}
                agentStatus={s.status}
                lastActivityAt={`2026-03-25T0${i + 1}:00:00Z`}
                showBorder={i > 0}
                expanded={expanded}
                inverted={expanded}
                onClick={() => onOpen("test", `ses_${i}`)}
              />
            </div>
          ))}
          <GhostSession onClick={() => onOpen("test")} inverted={expanded} />
        </div>
      </div>
      <img
        src="/frogs/working.gif"
        alt=""
        className="frog-debug"
        style={{
          position: "absolute",
          top: "var(--sp-2)",
          right: "var(--sp-3)",
          width: "24px",
          height: "24px",
          opacity: 0.65,
        }}
      />
    </div>
  );
}
